import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three'
import { FiLogOut } from "react-icons/fi";

function Viewer() {
  const navigate = useNavigate();
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const fileInputRef = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [inlinePosition, setInlinePosition] = useState(0);
  const [crosslinePosition, setCrosslinePosition] = useState(0);
  const [timeslicePosition, setTimeslicePosition] = useState(0);

  const [dimensions, setDimensions] = useState([0, 0, 0]);
  const [dataRange, setDataRange] = useState([0, 255]);
  const [colorScale, setColorScale] = useState(1.0);
  const [brightness, setBrightness] = useState(0.0);
  const [seismicData, setSeismicData] = useState(null);
  const [showInline, setShowInline] = useState(true);
  const [showCrossline, setShowCrossline] = useState(true);
  const [showTimeslice, setShowTimeslice] = useState(true);
  const [minPercentile, setMinPercentile] = useState(0);
  const [maxPercentile, setMaxPercentile] = useState(100);
  const [effectiveRange, setEffectiveRange] = useState([0, 255]);

  const [coordinateSystemRotation, setCoordinateSystemRotation] = useState(88.40);

  // Estados para cortes georeferenciados
  const [cutMode, setCutMode] = useState(false);
  const [cutStart, setCutStart] = useState({ i: 0, j: 0, k: 0 });
  const [cutEnd, setCutEnd] = useState({ i: 100, j: 100, k: 100 });
  const [showCutBox, setShowCutBox] = useState(false);
  const [cutBoxMesh, setCutBoxMesh] = useState(null);
  const [generatedCuts, setGeneratedCuts] = useState([]);

  const compassSceneRef = useRef(null);
  const compassCameraRef = useRef(null);
  const compassSizeRef = useRef({ w: 110, h: 110 });
  const compassGroupRef = useRef(null);
  const compassLabelsRef = useRef(null);

  const [meta, setMeta] = useState(null);

  const inlinePlaneRef = useRef(null);
  const crosslinePlaneRef = useRef(null);
  const timeslicePlaneRef = useRef(null);
  const wireframeRef = useRef(null);
  const cubeGroupRef = useRef(null);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  function logout(){
    localStorage.removeItem('logado');
    localStorage.removeItem('expiresAt');
    navigate('/');
  }

  // Função para calcular percentis
  function calculatePercentilesUint8(
    u8,
    pLow = 0.01,
    pHigh = 0.99,
    { ignoreZero = false, stride = 1 } = {}
  ) {
    const counts = new Uint32Array(256);
    let total = 0;
    for (let i = 0; i < u8.length; i += stride) {
      const v = u8[i];
      if (ignoreZero && v === 0) continue;
      counts[v]++; total++;
    }
    if (total === 0) return [0, 255];
    const tLow = Math.ceil(pLow * total);
    const tHigh = Math.ceil(pHigh * total);

    let csum = 0, p1 = 0, p99 = 255;
    for (let v = 0; v < 256; v++) { csum += counts[v]; if (csum >= tLow) { p1 = v; break; } }
    csum = 0;
    for (let v = 0; v < 256; v++) { csum += counts[v]; if (csum >= tHigh) { p99 = v; break; } }
    return [p1, p99];
  }

  // Função para criar caixa de corte visual
  const createCutBox = () => {
    if (!cubeGroupRef.current) return;

    // Remove caixa anterior se existir
    if (cutBoxMesh) {
      cubeGroupRef.current.remove(cutBoxMesh);
      cutBoxMesh.geometry?.dispose();
      cutBoxMesh.material?.dispose();
    }

    const [ni, nj, nk] = dimensions;
    const scale = 0.01;

    // Calcula dimensões e posição da caixa de corte
    const startI = Math.min(cutStart.i, cutEnd.i);
    const endI = Math.max(cutStart.i, cutEnd.i);
    const startJ = Math.min(cutStart.j, cutEnd.j);
    const endJ = Math.max(cutStart.j, cutEnd.j);
    const startK = Math.min(cutStart.k, cutEnd.k);
    const endK = Math.max(cutStart.k, cutEnd.k);

    const sizeI = (endI - startI + 1) * scale;
    const sizeJ = (endJ - startJ + 1) * scale;
    const sizeK = (endK - startK + 1) * scale;

    const centerI = ((startI + endI) / 2 / (ni - 1) - 0.5) * (ni * scale);
    const centerJ = -((startJ + endJ) / 2 / (nj - 1) - 0.5) * (nj * scale);
    const centerK = -((startK + endK) / 2 / (nk - 1) - 0.5) * (nk * scale);

    // Cria geometria da caixa
    const geometry = new THREE.BoxGeometry(sizeI, sizeJ, sizeK);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({ 
      color: 0xff4444, 
      linewidth: 3,
      transparent: true,
      opacity: 0.8
    });

    const wireframe = new THREE.LineSegments(edges, material);
    wireframe.position.set(centerI, centerJ, centerK);
    
    cubeGroupRef.current.add(wireframe);
    setCutBoxMesh(wireframe);
  };

  // Função para extrair subcubo
  const extractSubcube = () => {
    if (!seismicData || !meta) return null;

    const [ni, nj, nk] = dimensions;
    const idx = makeIndexer(dimensions, meta.flatten_order || 'C');

    const startI = Math.min(cutStart.i, cutEnd.i);
    const endI = Math.max(cutStart.i, cutEnd.i);
    const startJ = Math.min(cutStart.j, cutEnd.j);
    const endJ = Math.max(cutStart.j, cutEnd.j);
    const startK = Math.min(cutStart.k, cutEnd.k);
    const endK = Math.max(cutStart.k, cutEnd.k);

    const newNi = endI - startI + 1;
    const newNj = endJ - startJ + 1;
    const newNk = endK - startK + 1;

    const subcubeData = new Uint8Array(newNi * newNj * newNk);
    const newIdx = makeIndexer([newNi, newNj, newNk], meta.flatten_order || 'C');

    let index = 0;
    for (let i = startI; i <= endI; i++) {
      for (let j = startJ; j <= endJ; j++) {
        for (let k = startK; k <= endK; k++) {
          const origIndex = idx(i, j, k);
          subcubeData[index] = seismicData[origIndex];
          index++;
        }
      }
    }

    // Cria metadados do subcubo com informações georeferenciadas
    const subcubeMeta = {
      ...meta,
      shape: [newNi, newNj, newNk],
      parent_shape: dimensions,
      cut_origin: { i: startI, j: startJ, k: startK },
      cut_end: { i: endI, j: endJ, k: endK },
      coordinate_system_rotation: coordinateSystemRotation,
      created_at: new Date().toISOString(),
      is_subcube: true,
      parent_file: meta.original_filename || 'unknown'
    };

    return { data: subcubeData, meta: subcubeMeta };
  };

  // Função para gerar e baixar subcubo
  const generateCut = () => {
    const subcube = extractSubcube();
    if (!subcube) {
      alert('Erro ao extrair subcubo');
      return;
    }

    const { data, meta: subcubeMeta } = subcube;
    const [newNi, newNj, newNk] = subcubeMeta.shape;

    // Cria nome do arquivo baseado nas coordenadas
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const basename = `subcube_${subcubeMeta.cut_origin.i}-${subcubeMeta.cut_end.i}_${subcubeMeta.cut_origin.j}-${subcubeMeta.cut_end.j}_${subcubeMeta.cut_origin.k}-${subcubeMeta.cut_end.k}_${timestamp}`;

    // Download do arquivo .grva
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${basename}.grva`;
    a.click();
    URL.revokeObjectURL(url);

    // Download do arquivo .json com metadados
    const jsonBlob = new Blob([JSON.stringify(subcubeMeta, null, 2)], { type: 'application/json' });
    const jsonUrl = URL.createObjectURL(jsonBlob);
    const jsonA = document.createElement('a');
    jsonA.href = jsonUrl;
    jsonA.download = `${basename}.json`;
    jsonA.click();
    URL.revokeObjectURL(jsonUrl);

    // Adiciona à lista de cortes gerados
    const cutInfo = {
      id: Date.now(),
      name: basename,
      shape: [newNi, newNj, newNk],
      origin: subcubeMeta.cut_origin,
      end: subcubeMeta.cut_end,
      size: data.length,
      timestamp
    };

    setGeneratedCuts(prev => [...prev, cutInfo]);
    
    alert(`Subcubo extraído com sucesso!\nDimensões: ${newNi}×${newNj}×${newNk}\nTamanho: ${(data.length / 1024 / 1024).toFixed(2)} MB`);
  };

  // Função para visualizar apenas o subcubo
  const visualizeSubcube = async () => {
    const subcube = extractSubcube();
    if (!subcube) {
      alert('Erro ao extrair subcubo');
      return;
    }

    const { data, meta: subcubeMeta } = subcube;
    
    // Carrega o subcubo na visualização atual
    await loadRawSeismicDataWithMeta(data.buffer, subcubeMeta);
    
    alert('Visualizando subcubo extraído. Use "Voltar ao Original" para retornar ao cubo completo.');
  };

  // Função para voltar ao cubo original
  const returnToOriginal = () => {
    if (meta && meta.original_data) {
      loadRawSeismicDataWithMeta(meta.original_data, meta.original_meta);
    }
  };

  // Inicialização do Three.js
  useEffect(() => {
    if (!mountRef.current) return;

    if (localStorage.getItem('logado') !== '1') {
        navigate('/');
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);

    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(8, -8, 6);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    mountRef.current.appendChild(renderer.domElement);

    const cubeGroup = new THREE.Group();
    scene.add(cubeGroup);
    cubeGroupRef.current = cubeGroup;

    // Interação
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };
    const onMouseDown = (event) => { isDragging = true; previousMousePosition = { x: event.clientX, y: event.clientY }; };
    const onMouseMove = (event) => {
      if (!isDragging) return;
      const deltaX = event.clientX - previousMousePosition.x;
      const deltaY = event.clientY - previousMousePosition.y;
      const spherical = new THREE.Spherical().setFromVector3(camera.position);
      spherical.theta -= deltaX * 0.01;
      spherical.phi += deltaY * 0.01;
      spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
      camera.position.setFromSpherical(spherical);
      camera.lookAt(0, 0, 0);
      previousMousePosition = { x: event.clientX, y: event.clientY };
    };
    const onMouseUp = () => { isDragging = false; };
    const onWheel = (event) => {
      const spherical = new THREE.Spherical().setFromVector3(camera.position);
      spherical.radius += event.deltaY * 0.01;
      spherical.radius = Math.max(3, Math.min(50, spherical.radius));
      camera.position.setFromSpherical(spherical);
      camera.lookAt(0, 0, 0);
    };

    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('wheel', onWheel);

    scene.add(new THREE.AmbientLight(0x404040, 0.8));
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(1, 1, 1);
    scene.add(dir);

    // Loop de animação
    let animId = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      renderer.render(scene, camera);

      // Render do compass (código do compass mantido)
      const inset = compassSizeRef.current;
      const r = renderer;
      const cnv = r.domElement;

      if (camera && compassGroupRef.current && compassLabelsRef.current) {
        const inv = camera.quaternion.clone().invert();
        compassGroupRef.current.quaternion.copy(inv);
        compassLabelsRef.current.quaternion.copy(inv);
      }

      if (compassCameraRef.current) {
        compassCameraRef.current.position.set(0, 0, 6);
        compassCameraRef.current.lookAt(0, 0, 0);
        compassCameraRef.current.up.set(0, 1, 0);
        compassCameraRef.current.aspect = 1;
        compassCameraRef.current.updateProjectionMatrix();
      }

      r.autoClear = false;
      r.clearDepth();
      r.setScissorTest(true);
      const x = cnv.width - inset.w - 10;
      const y = 10;
      r.setViewport(x, y, inset.w, inset.h);
      r.setScissor(x, y, inset.w, inset.h);
      r.setClearColor(0x000000, 1);
      r.clear(true, true, true);

      if (compassCameraRef.current && compassSceneRef.current) {
        r.render(compassSceneRef.current, compassCameraRef.current);
      }

      r.setScissorTest(false);
      r.setViewport(0, 0, cnv.width, cnv.height);
      r.autoClear = true;
    };

    animate();

    const handleResize = () => {
      if (!mountRef.current) return;
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;

    // Compass setup (código do compass mantido)
    const compassScene = new THREE.Scene();
    compassScene.background = new THREE.Color(0x2f2f2f);

    const half = 1.6;
    const aspect = 1;
    const compassCam = new THREE.OrthographicCamera(
      -half*aspect, half*aspect,
      half, -half,
      0.1, 100
    );
    compassCam.position.set(0, 0, 6);
    compassCam.lookAt(0, 0, 0);

    const amb = new THREE.AmbientLight(0xffffff, 0.6);
    const dirL = new THREE.DirectionalLight(0xffffff, 0.8);
    dirL.position.set(2, 3, 4);
    compassScene.add(amb, dirL);

    const axisLen = half * 0.9;
    const shaftGeom = new THREE.CylinderGeometry(0.05, 0.05, axisLen-0.35, 16);
    const headGeom  = new THREE.ConeGeometry(0.12, 0.30, 24);

    const mRedS   = new THREE.MeshPhongMaterial({ color: 0xcc3333 });
    const mGreenS = new THREE.MeshPhongMaterial({ color: 0x33aa33 });
    const mBlueS  = new THREE.MeshPhongMaterial({ color: 0x2d7dd2 });
    const mHead   = new THREE.MeshPhongMaterial({ color: 0x111111 });

    const origin = new THREE.Mesh(
      new THREE.SphereGeometry(0.10, 24, 16),
      new THREE.MeshPhongMaterial({ color: 0xffcc00, emissive: 0x222200 })
    );

    const xShaft = new THREE.Mesh(shaftGeom, mGreenS);
    xShaft.rotation.z = -Math.PI/2;
    xShaft.position.x = (axisLen/2) - 0.175;
    const xHead = new THREE.Mesh(headGeom, mHead);
    xHead.rotation.z = -Math.PI/2;
    xHead.position.x = axisLen - 0.15;

    const yShaft = new THREE.Mesh(shaftGeom, mRedS);
    yShaft.position.y = (axisLen/2) - 0.175;
    const yHead = new THREE.Mesh(headGeom, mHead);
    yHead.position.y = axisLen - 0.15;

    const zShaft = new THREE.Mesh(shaftGeom, mBlueS);
    zShaft.rotation.x = Math.PI/2;
    zShaft.position.z = -(axisLen/2) + 0.175;
    const zHead = new THREE.Mesh(headGeom, mHead);
    zHead.rotation.x = -Math.PI/2;
    zHead.position.z = -axisLen + 0.15;

    const axisGroup = new THREE.Group();
    axisGroup.add(xShaft, xHead, yShaft, yHead, zShaft, zHead, origin);

    const labelScale = 0.42;
    function makeTextSprite(text) {
      const c = document.createElement('canvas'); const s = 256;
      c.width = c.height = s;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 160px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, s/2, s/2);
      const tex = new THREE.CanvasTexture(c);
      tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
      const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false });
      const spr = new THREE.Sprite(mat);
      spr.scale.set(labelScale, labelScale, labelScale);
      spr.renderOrder = 999;
      return spr;
    }
    const labelE = makeTextSprite('E');
    const labelN = makeTextSprite('N');
    const labelZ = makeTextSprite('Z');

    labelE.position.set(axisLen - 0.05, 0.0, 0.0);
    labelN.position.set(0.0, axisLen - 0.05, 0.0);
    labelZ.position.set(0.0, -0.05, -(axisLen - 0.05));

    const labels = new THREE.Group();
    labels.add(labelE, labelN, labelZ);

    compassScene.add(axisGroup, labels);

    compassSceneRef.current  = compassScene;
    compassCameraRef.current = compassCam;
    compassGroupRef.current  = axisGroup;
    compassLabelsRef.current = labels;

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      renderer.domElement.removeEventListener('mousemove', onMouseMove);
      renderer.domElement.removeEventListener('mouseup', onMouseUp);
      renderer.domElement.removeEventListener('wheel', onWheel);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  // Aplicar rotação do sistema de coordenadas
  useEffect(() => {
    if (cubeGroupRef.current) {
      const rotationRadians = (coordinateSystemRotation * Math.PI) / 180;
      cubeGroupRef.current.rotation.set(0, 0, rotationRadians);
    }
  }, [coordinateSystemRotation]);

  // Wireframe do volume
  useEffect(() => {
    if (!cubeGroupRef.current) return;

    if (wireframeRef.current) {
      cubeGroupRef.current.remove(wireframeRef.current);
      wireframeRef.current.geometry.dispose?.();
      wireframeRef.current.material.dispose?.();
      wireframeRef.current = null;
    }

    const [ni, nj, nk] = dimensions;
    const scale = 0.01;
    const geom = new THREE.BoxGeometry(ni * scale, nj * scale, nk * scale);
    const edges = new THREE.EdgesGeometry(geom);
    const mat = new THREE.LineBasicMaterial({ color: 0x888888 });
    const wire = new THREE.LineSegments(edges, mat);
    cubeGroupRef.current.add(wire);
    wireframeRef.current = wire;
  }, [dimensions]);

  // Atualizar caixa de corte quando parâmetros mudam
  useEffect(() => {
    if (showCutBox && cutMode) {
      createCutBox();
    } else if (cutBoxMesh) {
      cubeGroupRef.current?.remove(cutBoxMesh);
      cutBoxMesh.geometry?.dispose();
      cutBoxMesh.material?.dispose();
      setCutBoxMesh(null);
    }
  }, [showCutBox, cutMode, cutStart, cutEnd, dimensions]);

  // Ajustar limites dos controles de corte quando dimensões mudam
  useEffect(() => {
    const [ni, nj, nk] = dimensions;
    if (ni > 0 && nj > 0 && nk > 0) {
      setCutStart({ i: 0, j: 0, k: 0 });
      setCutEnd({ i: Math.min(100, ni-1), j: Math.min(100, nj-1), k: Math.min(100, nk-1) });
    }
  }, [dimensions]);

  function makeIndexer(shape, flattenOrder = 'C') {
    const [ni, nj, nk] = shape;
    const order = (flattenOrder || 'C').toUpperCase();
    if (order === 'F') {
      return (i, j, k) => i + j * ni + k * ni * nj;
    }
    return (i, j, k) => i * nj * nk + j * nk + k;
  }

  const handleFileLoad = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const rawFile = files.find(f => /\.(grva)$/i.test(f.name));

    if (!rawFile) {
      alert('Selecione o arquivo .grva (e preferencialmente o .json junto).');
      return;
    }

    const base = rawFile.name.replace(/\.(grva)$/i, '');
    const exactJsonName = `${base}.json`.toLowerCase();

    console.log('Procurando JSON com nome exato:', exactJsonName);

    let jsonFile =
      files.find(f => f.name.toLowerCase() === exactJsonName) ||
      files.find(f => /\.json$/i.test(f.name));

    const rawBuf = await rawFile.arrayBuffer();

    let metaFromFile = null;
    if (jsonFile) {
      try { 
        metaFromFile = JSON.parse(await jsonFile.text()); 
        metaFromFile.original_filename = rawFile.name;
      }
      catch (e) { 
        console.error('JSON inválido:', e); 
        alert('Não consegui ler o .json; vou tentar defaults.'); 
      }
    }

    await loadRawSeismicDataWithMeta(rawBuf, metaFromFile);
  };

  async function loadRawSeismicDataWithMeta(arrayBuffer, metaFile) {
    let shape = [651, 951, 462];
    let axisOrder = ['iline', 'xline', 'samples'];
    let flattenOrder = 'C';
    let rotation = 88.40;

    if (metaFile && Array.isArray(metaFile.shape)) {
      shape = metaFile.shape.slice(0, 3).map(Number);
      axisOrder = Array.isArray(metaFile.axis_order) ? metaFile.axis_order : axisOrder;
      flattenOrder = (metaFile.flatten_order || 'C').toUpperCase();
      if (typeof metaFile.rotation === 'number') {
        rotation = metaFile.rotation;
      }
      
      // Preserva dados originais se não for subcubo
      if (!metaFile.is_subcube && !metaFile.original_data) {
        metaFile.original_data = arrayBuffer;
        metaFile.original_meta = { ...metaFile };
      }
      
      setMeta(metaFile);
    } else {
      const newMeta = { 
        dtype: 'uint8', 
        shape, 
        axis_order: axisOrder, 
        flatten_order: flattenOrder, 
        rotation,
        original_data: arrayBuffer,
        original_meta: null
      };
      setMeta(newMeta);
    }

    const [ni, nj, nk] = shape;
    const total = ni * nj * nk;

    const u8 = new Uint8Array(arrayBuffer);
    if (u8.length !== total) {
      alert(`Tamanho do arquivo: ${u8.length}\nEsperado: ${total} (${ni} × ${nj} × ${nk})`);
      return;
    }

    setSeismicData(u8);
    setDimensions(shape);
    setDataRange([0, 255]);
    setCoordinateSystemRotation(rotation);

    const stride = u8.length > 50_000_000 ? 4 : 1;
    const [p1, p99] = calculatePercentilesUint8(u8, 0.01, 0.99, { ignoreZero: true, stride });
    setEffectiveRange([p1, p99]);

    setInlinePosition(Math.floor(ni / 2));
    setCrosslinePosition(Math.floor(nj / 2));
    setTimeslicePosition(Math.floor(nk / 2));
    setIsLoaded(true);

    const resetView = () => {
      const cam = cameraRef.current;
      if (!cam) return;
      const scale = 0.01;
      const r = Math.max(nj*scale, ni*scale, nk*scale) * 2.0;
      cam.position.set(0, -r, r * 0.6);
      cam.lookAt(0, 0, 0);
    };
    resetView();
  }

  const createSliceTexture = (data, shape, mode, position) => {
    const [ni, nj, nk] = shape;
    const idx = makeIndexer(shape, meta?.flatten_order || 'C');

    let width, height, sliceData;
    const minVal = effectiveRange[0];
    const maxVal = effectiveRange[1];
    const invRange = maxVal > minVal ? 1 / (maxVal - minVal) : 1;

    const applyToneMap = (v) => {
      let g = (v - minVal) * invRange;
      g = Math.max(0, Math.min(1, g));
      let out = g * 255 * colorScale + brightness * 255;
      out = Math.max(0, Math.min(255, out));
      return out | 0;
    };

    switch (mode) {
      case 'inline': {
        const i = clamp(position, 0, ni - 1);
        width = nk;
        height = nj;
        sliceData = new Uint8Array(width * height * 4);
        for (let j = 0; j < nj; j++) {
          for (let k = 0; k < nk; k++) {
            const value = data[idx(i, j, k)];
            const gray = applyToneMap(value);
            const px = ((height - 1 - j) * width + k) * 4;
            sliceData[px] = sliceData[px + 1] = sliceData[px + 2] = gray;
            sliceData[px + 3] = 255;
          }
        }
        break;
      }
      case 'crossline': {
        const j = clamp(position, 0, nj - 1);
        width = ni;
        height = nk;
        sliceData = new Uint8Array(width * height * 4);
        
        for (let k = 0; k < nk; k++) {
          for (let i = 0; i < ni; i++) {
            const value = data[idx(i, j, k)];
            const gray = applyToneMap(value);
            const px = ((height - 1 - k) * width + i) * 4;
            sliceData[px] = sliceData[px + 1] = sliceData[px + 2] = gray;
            sliceData[px + 3] = 255;
          }
        }
        break;
      }
      case 'timeslice': {
        const k = clamp(position, 0, nk - 1);
        width = ni;
        height = nj;
        sliceData = new Uint8Array(width * height * 4);
        for (let j = 0; j < nj; j++) {
          for (let i = 0; i < ni; i++) { 
            const value = data[idx(i, j, k)];
            const gray = applyToneMap(value);
            const px = ((height - 1 - j) * width + i) * 4;
            sliceData[px] = sliceData[px + 1] = sliceData[px + 2] = gray;
            sliceData[px + 3] = 255;
          }
        }
        break;
      }
      default:
        return { texture: null, width: 0, height: 0 };
    }

    const texture = new THREE.DataTexture(sliceData, width, height, THREE.RGBAFormat);
    texture.needsUpdate = true;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    return { texture, width, height };
  };

  const removePlane = (ref) => {
    const mesh = ref.current;
    if (mesh && cubeGroupRef.current) {
      cubeGroupRef.current.remove(mesh);
      if (mesh.material?.map) mesh.material.map.dispose();
      mesh.material?.dispose?.();
      mesh.geometry?.dispose?.();
      ref.current = null;
    }
  };

  const updateVisualization = () => {
    if (!seismicData || !cubeGroupRef.current) return;

    removePlane(inlinePlaneRef);
    removePlane(crosslinePlaneRef);
    removePlane(timeslicePlaneRef);

    const [ni, nj, nk] = dimensions;
    const scale = 0.01;

    if (showInline) {
      const { texture, width, height } =
        createSliceTexture(seismicData, dimensions, 'inline', inlinePosition);
      const geom = new THREE.PlaneGeometry(width * scale, height * scale);
      const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, opacity: 0.95 });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.rotation.y = Math.PI / 2;
      mesh.position.x = ((inlinePosition / (ni - 1)) - 0.5) * (ni * scale);
      cubeGroupRef.current.add(mesh);
      inlinePlaneRef.current = mesh;
    }

    if (showCrossline) {
      const { texture, width, height } =
        createSliceTexture(seismicData, dimensions, 'crossline', crosslinePosition);
      const geom = new THREE.PlaneGeometry(width * scale, height * scale);
      const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, opacity: 0.95 });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.rotation.x = Math.PI / 2;
      mesh.position.y = -((crosslinePosition / (nj - 1)) - 0.5) * (nj * scale);
      
      cubeGroupRef.current.add(mesh);
      crosslinePlaneRef.current = mesh;
    }

    if (showTimeslice) {
      const { texture, width, height } =
        createSliceTexture(seismicData, dimensions, 'timeslice', timeslicePosition);
      const geom = new THREE.PlaneGeometry(width * scale, height * scale);
      const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, opacity: 0.95 });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.z = -(((timeslicePosition / (nk - 1)) - 0.5) * (nk * scale));
      cubeGroupRef.current.add(mesh);
      timeslicePlaneRef.current = mesh;
    }
  };

  useEffect(() => {
    if (!seismicData) return;
    let pLow = clamp(minPercentile, 0, 100) / 100;
    let pHigh = clamp(maxPercentile, 0, 100) / 100;
    if (pLow >= pHigh) {
      pLow = Math.max(0, Math.min(pLow, 0.99));
      pHigh = Math.min(1, Math.max(pLow + 0.01, pHigh));
    }
    const stride = seismicData.length > 50_000_000 ? 4 : 1;
    const [lo, hi] = calculatePercentilesUint8(seismicData, pLow, pHigh, { ignoreZero: true, stride });
    setEffectiveRange([lo, hi]);
  }, [minPercentile, maxPercentile, seismicData]);

  useEffect(() => {
    updateVisualization();
  }, [
    seismicData,
    inlinePosition,
    crosslinePosition,
    timeslicePosition,
    colorScale,
    brightness,
    showInline,
    showCrossline,
    showTimeslice,
    effectiveRange,
    meta,
  ]);

  const dimsLabel = meta?.shape ? meta.shape.join(' × ') : `${dimensions[0]}×${dimensions[1]}×${dimensions[2]}`;

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', color: 'black' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />

      <div style={{backgroundColor: "rgba(255, 255, 255, 0.85)", borderRadius: '8px', position: 'absolute', top: '10px', right: "55px", width: '110px', height: '60px'}}>
        <a href={"https://grva.lamce.coppe.ufrj.br/"} target="_blank" rel="noopener noreferrer">
            <img src={"LAMCE_WEB.png"} alt="Info" style={{mixBlendMode: "multiply", width: '100px', verticalAlign: 'middle', marginTop:'6px', marginLeft: '6px',  }} />
        </a>
      </div>
      <div style={{backgroundColor: "rgba(255, 255, 255, 0.85)", borderRadius: '8px', position: 'absolute', top: '10px', right: "10px", paddingLeft: '5px', width: '32px', height: '25px'}}>
        <FiLogOut className="text-gray-500 hover:text-red-500 transition"  onClick={logout} style={{cursor: 'pointer', paddingTop: '4px', paddingLeft: '4px' }} aria-label="Sair"/>

      </div>
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          maxWidth: '320px',
          maxHeight: '95vh',
          minHeight: '95vh',
          overflowY: 'auto',
        }}
      >
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
          Cubo Sísmico 3D {meta?.is_subcube && '(Subcubo)'}
        </h3>

        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
            Carregar arquivos (.grva + .json):
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".grva,.json"
            multiple
            onChange={handleFileLoad}
            style={{ width: '100%' }}
          />
          <small style={{ color: '#666', fontSize: '11px' }}>
            Dimensões: {dimsLabel} &nbsp; • &nbsp; Flatten: {meta?.flatten_order || 'C'} &nbsp; • &nbsp; Rotação XY: {coordinateSystemRotation.toFixed(1)}°
          </small>
          {meta?.is_subcube && (
            <div style={{ marginTop: '5px', padding: '5px', background: '#e8f4fd', borderRadius: '3px', fontSize: '11px' }}>
              <strong>Subcubo:</strong> Origem ({meta.cut_origin?.i}, {meta.cut_origin?.j}, {meta.cut_origin?.k})
              {meta.original_data && (
                <button
                  onClick={returnToOriginal}
                  style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    fontSize: '10px',
                    background: '#007acc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                >
                  Voltar ao Original
                </button>
              )}
            </div>
          )}
        </div>

        {isLoaded ? (
          <>
            
            
            <div style={{ marginBottom: '15px', padding: '12px', background: '#fff5f5', borderRadius: '4px', border: '1px solid #ffcccc' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontSize: '14px', fontWeight: 'bold', marginRight: '10px' }}>
                  <input
                    type="checkbox"
                    checked={cutMode}
                    onChange={(e) => {
                      setCutMode(e.target.checked);
                      if (!e.target.checked) {
                        setShowCutBox(false);
                      }
                    }}
                    style={{ marginRight: '5px' }}
                  />
                  Modo de Corte
                </label>
              </div>

              {cutMode && (
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '12px', marginBottom: '5px', display: 'block' }}>
                      <input
                        type="checkbox"
                        checked={showCutBox}
                        onChange={(e) => setShowCutBox(e.target.checked)}
                        style={{ marginRight: '5px' }}
                      />
                      Mostrar Caixa de Corte
                    </label>
                  </div>

                  
                  <div style={{ marginBottom: '10px', padding: '8px', background: '#f0f0f0', borderRadius: '3px' }}>
                    <strong style={{ fontSize: '12px' }}>Início do Corte:</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px', marginTop: '5px' }}>
                      <div>
                        <label style={{ fontSize: '10px' }}>I: {cutStart.i}</label>
                        <input
                          type="range"
                          min="0"
                          max={dimensions[0] - 1}
                          value={cutStart.i}
                          onChange={(e) => setCutStart(prev => ({ ...prev, i: Number(e.target.value) }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '10px' }}>J: {cutStart.j}</label>
                        <input
                          type="range"
                          min="0"
                          max={dimensions[1] - 1}
                          value={cutStart.j}
                          onChange={(e) => setCutStart(prev => ({ ...prev, j: Number(e.target.value) }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '10px' }}>K: {cutStart.k}</label>
                        <input
                          type="range"
                          min="0"
                          max={dimensions[2] - 1}
                          value={cutStart.k}
                          onChange={(e) => setCutStart(prev => ({ ...prev, k: Number(e.target.value) }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </div>

                  
                  <div style={{ marginBottom: '10px', padding: '8px', background: '#f0f0f0', borderRadius: '3px' }}>
                    <strong style={{ fontSize: '12px' }}>Fim do Corte:</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px', marginTop: '5px' }}>
                      <div>
                        <label style={{ fontSize: '10px' }}>I: {cutEnd.i}</label>
                        <input
                          type="range"
                          min="0"
                          max={dimensions[0] - 1}
                          value={cutEnd.i}
                          onChange={(e) => setCutEnd(prev => ({ ...prev, i: Number(e.target.value) }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '10px' }}>J: {cutEnd.j}</label>
                        <input
                          type="range"
                          min="0"
                          max={dimensions[1] - 1}
                          value={cutEnd.j}
                          onChange={(e) => setCutEnd(prev => ({ ...prev, j: Number(e.target.value) }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '10px' }}>K: {cutEnd.k}</label>
                        <input
                          type="range"
                          min="0"
                          max={dimensions[2] - 1}
                          value={cutEnd.k}
                          onChange={(e) => setCutEnd(prev => ({ ...prev, k: Number(e.target.value) }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </div>

                  
                  <div style={{ fontSize: '11px', color: '#666', marginBottom: '10px', padding: '5px', background: '#f9f9f9', borderRadius: '3px' }}>
                    <strong>Dimensões do Corte:</strong><br/>
                    {Math.abs(cutEnd.i - cutStart.i) + 1} × {Math.abs(cutEnd.j - cutStart.j) + 1} × {Math.abs(cutEnd.k - cutStart.k) + 1}<br/>
                    <strong>Tamanho:</strong> {((Math.abs(cutEnd.i - cutStart.i) + 1) * (Math.abs(cutEnd.j - cutStart.j) + 1) * (Math.abs(cutEnd.k - cutStart.k) + 1) / 1024 / 1024).toFixed(2)} MB
                  </div>

                  
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button
                      onClick={visualizeSubcube}
                      style={{
                        padding: '8px 12px',
                        fontSize: '11px',
                        background: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        flex: 1
                      }}
                    >
                      Visualizar Subcubo
                    </button>
                    <button
                      onClick={generateCut}
                      style={{
                        padding: '8px 12px',
                        fontSize: '11px',
                        background: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        flex: 1
                      }}
                    >
                      Exportar Subcubo
                    </button>
                  </div>
                </div>
              )}
            </div>

           
            {generatedCuts.length > 0 && (
              <div style={{ marginBottom: '15px', padding: '10px', background: '#f0f8ff', borderRadius: '4px', border: '1px solid #ddeeff' }}>
                <strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Subcubos Exportados ({generatedCuts.length}):
                </strong>
                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                  {generatedCuts.map((cut, index) => (
                    <div key={cut.id} style={{ 
                      fontSize: '10px', 
                      marginBottom: '5px', 
                      padding: '5px', 
                      background: 'white', 
                      borderRadius: '3px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <strong>#{index + 1}:</strong> {cut.shape.join('×')}<br/>
                      <span style={{ color: '#666' }}>
                        ({cut.origin.i},{cut.origin.j},{cut.origin.k}) → ({cut.end.i},{cut.end.j},{cut.end.k})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

           
            <div style={{ marginBottom: '15px', fontSize: '12px', color: '#666', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
              <div><strong>Shape:</strong> {dimsLabel}</div>
              <div><strong>Range Original:</strong> [{dataRange[0]}, {dataRange[1]}]</div>
              <div><strong>Range Efetivo:</strong> [{effectiveRange[0]}, {effectiveRange[1]}]</div>
              <div><strong>Rotação Sistema XY:</strong> {coordinateSystemRotation.toFixed(1)}°</div>
              {meta?.axis_order && <div><strong>Eixos:</strong> {meta.axis_order.join(', ')}</div>}
            </div>

            
            <div style={{ marginBottom: '15px', padding: '12px', background: '#fff0f5', borderRadius: '4px', border: '1px solid #ffb6c1' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>
                Rotação Sistema X-Y: {coordinateSystemRotation.toFixed(1)}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                step="0.1"
                value={coordinateSystemRotation}
                onChange={(e) => setCoordinateSystemRotation(Number(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>

            
            <div style={{ marginBottom: '15px', padding: '12px', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>
                Ajuste de Intensidade (Percentis)
              </label>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>
                  Percentil Mínimo: {minPercentile}% (Valor: {effectiveRange[0]})
                </label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={minPercentile}
                  onChange={(e) => setMinPercentile(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>
                  Percentil Máximo: {maxPercentile}% (Valor: {effectiveRange[1]})
                </label>
                <input
                  type="range"
                  min="50"
                  max="100"
                  value={maxPercentile}
                  onChange={(e) => setMaxPercentile(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                Planos Visíveis:
              </label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showInline} onChange={(e) => setShowInline(e.target.checked)} style={{ marginRight: 4 }} />
                  Inline (XZ)
                </label>
                <label style={{ fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showCrossline} onChange={(e) => setShowCrossline(e.target.checked)} style={{ marginRight: 4 }} />
                  Crossline (YZ)
                </label>
                <label style={{ fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showTimeslice} onChange={(e) => setShowTimeslice(e.target.checked)} style={{ marginRight: 4 }} />
                  Timeslice (XY)
                </label>
              </div>
            </div>

            
            <div style={{ marginBottom: '15px', padding: '10px', background: '#f0f8ff', borderRadius: '4px' }}>
              <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 'bold', color: '#0066cc' }}>
                Inline (i): {inlinePosition} / {dimensions[0] - 1}
              </label>
              <input
                type="range"
                min="0"
                max={dimensions[0] - 1}
                value={inlinePosition}
                onChange={(e) => setInlinePosition(Number(e.target.value))}
                style={{ width: '100%' }}
                disabled={!showInline}
              />
            </div>

            <div style={{ marginBottom: '15px', padding: '10px', background: '#f0fff0', borderRadius: '4px' }}>
              <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 'bold', color: '#228B22' }}>
                Crossline (j): {crosslinePosition} / {dimensions[1] - 1}
              </label>
              <input
                type="range"
                min="0"
                max={dimensions[1] - 1}
                value={crosslinePosition}
                onChange={(e) => setCrosslinePosition(Number(e.target.value))}
                style={{ width: '100%' }}
                disabled={!showCrossline}
              />
            </div>

            <div style={{ marginBottom: '15px', padding: '10px', background: '#fff5f0', borderRadius: '4px' }}>
              <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 'bold', color: '#cc6600' }}>
                Timeslice (k): {timeslicePosition} / {dimensions[2] - 1}
              </label>
              <input
                type="range"
                min="0"
                max={dimensions[2] - 1}
                value={timeslicePosition}
                onChange={(e) => setTimeslicePosition(Number(e.target.value))}
                style={{ width: '100%' }}
                disabled={!showTimeslice}
              />
            </div>

            
            <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: 10, fontSize: 14, fontWeight: 'bold' }}>
                Ajustes de Visualização
              </label>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', marginBottom: 5, fontSize: 13 }}>
                  Contraste: {colorScale.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="3.0"
                  step="0.1"
                  value={colorScale}
                  onChange={(e) => setColorScale(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 5, fontSize: 13 }}>
                  Brilho: {brightness.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="-0.5"
                  max="0.5"
                  step="0.1"
                  value={brightness}
                  onChange={(e) => setBrightness(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </>
        ) : (
          <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', padding: '20px' }}>
            Carregue um arquivo .grva e (opcional) o .json de metadados.
          </div>
        )}
      </div>

      
      {isLoaded && (
        <div
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '365px',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '10px',
            borderRadius: '6px',
            fontSize: '11px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 5 }}>Tons de Cinza</div>
          <div style={{
            position: 'relative',
            width: 20,
            height: 80,
            background: 'linear-gradient(to top, #000000, #808080, #ffffff)',
            margin: '5px auto',
            border: '1px solid #ccc'
          }}> 
            <div style={{  
              position: 'absolute',
              top: 0,
              left: '100%',
              marginLeft: 4,
              fontSize: 10,
              color: '#000',
            }}
            >{effectiveRange[1]}</div>
            <div style={{ position: 'absolute',
            top: '50%',
            left: '100%',
            transform: 'translateY(-50%)',
            marginLeft: 4,
            fontSize: 10,
            color: '#000',}}>
              {Math.round((effectiveRange[0] + effectiveRange[1]) / 2)}
            </div>
            <div style={{  position: 'absolute',
            bottom: 0,
            left: '100%',
            marginLeft: 4,
            fontSize: 10,
            color: '#000', }}>{effectiveRange[0]}</div>
          </div>
        </div>
      )}
    </div>
  );
 
}

export default Viewer
